<script setup>
import { AtSymbolIcon } from "@heroicons/vue/outline"
import { Inertia } from "@inertiajs/inertia"
import { ref, computed } from "vue"
import { useAuthStore } from "@/Stores/auth"
const auth = useAuthStore()

import EditModelValues from "@/Components/Models/User/EditModelValues.vue";
import ResetPasswordButton from "@/Components/Models/User/ResetPasswordButton.vue";
import DeleteUserButton from "@/Components/Models/User/DeleteUserButton.vue";
import SetUserOrganisationPermissions from '@/Components/Models/User/Roles/SetUserOrganisationPermissions.vue'
import RelatedOrganisationsCard from '@/Components/Models/User/RelatedOrganisationsCard.vue'

const props = defineProps({
    user: {
        type: Object,
        required: true,
    },
	roles: {
		type: Array,
		required: true,
	},
	errors: {
		type: Object,
		required: true,
	},
	organisations: {
		type: Array,
		required: true,
	},
});

const emit = defineEmits([
    'update:users',
    'change-password',
    'set-permissions'
]);

function saveDetails() {
    emit("update:users", props.user);
}

function back() {
    Inertia.visit("/users/" + props.user.id);
}

function updatePassword() {
    emit("change-password", props.user);
}

function setPermissions(permissions) {
	emit('set-permissions', permissions)
}

function userDeleted() {
	// go back to the org list
	Inertia.visit("/organisations/" + props.user.organisations[0].id)
}

/**
 * The displayed user will have permissions on some organisations. We need to check if the currently authed user has `manage_contacts` on
 * any of those organisations. If so, the authed user is able to change the displayed users details.
 */
const can_manage_user = computed(() => {
    // if the user being displayed is the same one as the authed user, they can manage themselves
    if (props.user.id == auth.id) {
        return true;
    }

    // otherwise defer to our auth store
    return auth.can('manage_contacts', 'organisations', props.user.permissions.organisations.map(o => o.id))
});
</script>

<template>
    <SectionCard title="Edit Details">
        <form @submit.stop.prevent="saveDetails" class="grid grid-cols-1 md:grid-cols-2 gap-x-4">
            <div>
                <Label>Name</Label>
                <Input
                    v-model="user.name"
                    v-if="can_manage_user"
                    :errors="user.errors.name"
                    testid="name"
                />
                <p v-else>{{ user.name }}</p>
            </div>

            <div>
                <Label>Login Email</Label>
                <Input
                    v-model="user.email"
                    v-if="can_manage_user"
                    :errors="user.errors.email"
                    testid="email"
                />
                <p v-else>{{ user.email }}</p>
            </div>

            <div>
                <Label>Primary Phone Number</Label>
                <Input
                    v-model="user.primary_phone_number"
                    v-if="can_manage_user"
                    :errors="user.errors.primary_phone_number"
                    testid="primary_phone_number"
                />
                <p v-else>{{ user.primary_phone_number }}</p>
            </div>

            <input type="submit" class="hidden" />
        </form>

        <template #footer>
            <Button
                @click="saveDetails"
                type="button"
                color="blue"
                v-if="can_manage_user"
            >
                Save
            </Button>
        </template>
    </SectionCard>

	<RelatedOrganisationsCard
		:user="user"
	/>

    <SectionCard
        class="mt-4"
        title="Reset Password"
        v-if="can_manage_user"
		data-testid="reset-password-section"
    >
        <p class="text-sm mb-4">
            A password reset link with instructions will be sent to the email address provided.
        </p>

        <template #footer>
            <ResetPasswordButton :user="user" data-testid="reset-password-button" />
        </template>
    </SectionCard>

    <div v-for="organisation in user.organisations" :key="organisation.id" data-testid="change-role-section">
		<SectionCard
			class="mt-4"
			title="User Permissions"
			v-if="auth.can('manage_contacts', 'organisations', organisation.id)"
		>
			<h2 class="font-medium mb-2">{{organisation.name}}</h2>
			<SetUserOrganisationPermissions 
				:user="user"
				:roles="roles"				
				:organisation="organisation"
				@change-organisation="changeOrganisation"
				@set-permissions="setPermissions"
				:errors="errors?.permissions ?? null"
			/>
				
			<template #footer>
				<Button
					data-testid="update-permissions-btn"
                	@click="saveDetails"
					color="blue"
					type="button"
				>
					Update Permissions
				</Button>
			</template>
		</SectionCard>

		<div class="flex w-full items-end">
			<DeleteUserButton 
				:user="user"
				class="mt-5 ml-auto"
				data-testid="delete-user-button"
				@user-deleted="userDeleted"
			/>
		</div>
	</div>
</template>
